import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges, inject, effect } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { InventoryService } from '../../../../_servicios/inventory/inventory.service';
import { MessageService } from 'primeng/api';
import { ConfigService } from '../../../../_servicios/config.service';
import { EquipmentEventPrintService } from '../../../../_servicios/equipments/equipment-event-print.service';

@Component({
  selector: 'app-equipment-event-drawer',
  templateUrl: './equipment-event-drawer.component.html',
  styleUrls: ['./equipment-event-drawer.component.scss'],
  standalone: false
})
export class EquipmentEventDrawerComponent implements OnInit, OnChanges {
  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  @Input() eventType: 'MAINTENANCE' | 'CALIBRATION' = 'MAINTENANCE';
  @Input() equipmentId!: string;
  
  // Master data
  @Input() inventoryItems: any[] = [];
  @Input() vendors: any[] = [];
  @Input() recentEvents: any[] = [];
  @Input() internalConsumablesMap!: Map<string, any>;
  
  // Edit mode
  @Input() editData: any = null;
  @Input() initialTabIndex: number = 0;
  @Input() eventMode: 'SCHEDULE' | 'COMPLETE' = 'COMPLETE';
  @Input() equipment: any = null;

  @Output() onSaved = new EventEmitter<void>();

  eventForm!: FormGroup;
  loading = false;
  
  // Local state
  activeTab = 0;
  consumableTab = 0;
  selectedInternalItem: any = null;
  deferredTasks: any[] = [];
  loadingDeferred = false;
  
  currencyOptions = [
    { label: 'ARS', value: 'ARS' },
    { label: 'USD', value: 'USD' },
    { label: 'EUR', value: 'EUR' }
  ];

  get availableParentEvents(): any[] {
    return (this.recentEvents || []).filter(e => !this.editData || e.value !== this.editData.id);
  }
  
  // Services
  private fb = inject(FormBuilder);
  private inventoryService = inject(InventoryService);
  private messageService = inject(MessageService);
  private configService = inject(ConfigService);
  private printService = inject(EquipmentEventPrintService);

  ngOnInit() {
    this.initForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['visible'] && this.visible) {
      if (!this.eventForm) this.initForm();
      this.resetState();
      
      if (this.editData) {
        this.populateForm(this.editData);
      } else {
        this.loadDeferredTasks();
      }
      this.updateValidators();
    }
  }

  setEventMode(mode: 'SCHEDULE' | 'COMPLETE') {
    this.eventMode = mode;
    this.updateValidators();
  }

  updateValidators() {
    if (!this.eventForm) return;
    const durationCtrl = this.eventForm.get('duration_hours');
    if (this.eventMode === 'SCHEDULE') {
      durationCtrl?.clearValidators();
      durationCtrl?.enable({ emitEvent: false });
    } else {
      if (this.eventForm.get('end_date')?.value) {
        durationCtrl?.clearValidators();
        durationCtrl?.disable({ emitEvent: false });
      } else {
        durationCtrl?.setValidators(Validators.required);
        durationCtrl?.enable({ emitEvent: false });
      }
    }
    durationCtrl?.updateValueAndValidity({ emitEvent: false });

    const typeCtrl = this.eventForm.get('type');
    if (this.eventType === 'MAINTENANCE') {
      typeCtrl?.setValidators(Validators.required);
    } else {
      typeCtrl?.clearValidators();
    }
    typeCtrl?.updateValueAndValidity({ emitEvent: false });
  }

  initForm() {
    this.eventForm = this.fb.group({
      id: [null],
      event_date: [new Date(), Validators.required],
      description: [''],
      external_service: [false],
      supplierId: [null],
      notes: [''],
      cost: [null],
      cost_currency: ['ARS'],
      parentEventId: [null],
      duration_hours: [null, Validators.required],
      end_date: [null],
      auto_schedule_next: [true],
      replace_existing_scheduled: [false],
      consumables: this.fb.array([]),
      costs: this.fb.array([]),
      tasks: this.fb.array([]),

      // Maintenance specific
      type: [this.eventType === 'MAINTENANCE' ? 'PREVENTIVE' : null, this.eventType === 'MAINTENANCE' ? Validators.required : []],
      new_status: [null],

      // Calibration specific
      certificate_number: [''],
      result_approved: [true],
      performed_by: [''],
      approved_by: [''],
      next_date_manual: [null],
    });

    // Auto-calculate duration_hours from end_date
    this.eventForm.get('end_date')?.valueChanges.subscribe((endDate: Date | null) => {
      const durationCtrl = this.eventForm.get('duration_hours');
      if (endDate) {
        const startDate = this.eventForm.get('event_date')?.value as Date | null;
        if (startDate) {
          const diffMs = new Date(endDate).getTime() - new Date(startDate).getTime();
          const diffHours = Math.max(0, diffMs / 3600000);
          durationCtrl?.setValue(Math.round(diffHours * 100) / 100, { emitEvent: false });
        }
        durationCtrl?.clearValidators();
        durationCtrl?.disable({ emitEvent: false });
      } else {
        durationCtrl?.enable({ emitEvent: false });
        durationCtrl?.setValidators(Validators.required);
      }
      durationCtrl?.updateValueAndValidity({ emitEvent: false });
    });

    this.eventForm.get('event_date')?.valueChanges.subscribe((startDate: Date | null) => {
      const endDate = this.eventForm.get('end_date')?.value as Date | null;
      if (endDate && startDate) {
        const diffMs = new Date(endDate).getTime() - new Date(startDate).getTime();
        const diffHours = Math.max(0, diffMs / 3600000);
        this.eventForm.get('duration_hours')?.setValue(Math.round(diffHours * 100) / 100, { emitEvent: false });
      }
    });

    // Handle external service toggles
    this.eventForm.get('external_service')?.valueChanges.subscribe(val => {
      const supCtrl = this.eventForm.get('supplierId');
      if (val) {
        supCtrl?.setValidators(Validators.required);
      } else {
        supCtrl?.clearValidators();
        supCtrl?.setValue(null);
      }
      supCtrl?.updateValueAndValidity();
    });
  }

  resetState() {
    this.activeTab = this.initialTabIndex;
    this.consumableTab = 0;
    this.eventConsumables.clear();
    this.eventCosts.clear();
    this.eventForm.reset({ 
      event_date: new Date(), 
      type: this.eventType === 'MAINTENANCE' ? 'PREVENTIVE' : null, 
      external_service: false, 
      result_approved: true, 
      auto_schedule_next: true, 
      replace_existing_scheduled: false, 
      duration_hours: null, 
      end_date: null 
    });
    this.deferredTasks = [];
    (this.eventForm.get('tasks') as FormArray).clear();
  }

  populateForm(data: any) {
    this.eventForm.patchValue({
      id: data.id,
      event_date: new Date(data.event_date),
      end_date: data.end_date ? new Date(data.end_date) : null,
      description: data.description,
      external_service: data.external_service,
      supplierId: data.supplierId,
      notes: data.notes,
      cost: data.cost,
      cost_currency: data.cost_currency || 'ARS',
      parentEventId: data.parentEventId || null,
      duration_hours: data.duration_hours || null,
      auto_schedule_next: true,
      replace_existing_scheduled: false,
      
      // Maintenance
      type: data.type,
      new_status: data.new_status || null,

      // Calibration
      certificate_number: data.certificate_number,
      result_approved: data.result_approved !== false,
      performed_by: data.performed_by,
      approved_by: data.approved_by,
      next_date_manual: data.next_date_manual ? new Date(data.next_date_manual) : null,
    });

    if (data.consumables) {
      data.consumables.forEach((c: any) => {
        const itemObj = c.item || (this.internalConsumablesMap ? this.internalConsumablesMap.get(c.itemId) : null);
        const availableStock = itemObj ? ((itemObj.stock || 0) - (itemObj.reserved_stock || 0) - (itemObj.blocked_stock || 0) + (this.eventMode === 'SCHEDULE' ? (c.quantity || 0) : 0)) : null;
        this.eventConsumables.push(this.fb.group({
          itemId: [c.itemId],
          name: [itemObj?.name || c.name || ''],
          quantity: [c.quantity, [Validators.required, Validators.min(0.01)]],
          is_external: [c.is_external],
          stock: [itemObj?.stock || null],
          available_stock: [availableStock],
          unit_cost: [c.unit_cost || itemObj?.price || 0],
          unit_cost_currency: [c.unit_cost_currency || itemObj?.currency || 'ARS'],
          stockMovementId: [c.stockMovementId || null],
          movement_code: [c.stock_movement?.movement_code || null]
        }));
      });
    }

      if (data.costs) {
        const costsArray = this.eventForm.get('costs') as FormArray;
        data.costs.forEach((c: any) => {
          costsArray.push(this.fb.group({
            conceptId: [c.conceptId, Validators.required],
            amount: [c.amount, [Validators.required, Validators.min(0)]],
            currency: [c.currency || 'ARS']
          }));
        });
      }

      if (data.tasks) {
        const tasksArray = this.eventForm.get('tasks') as FormArray;
        data.tasks.forEach((t: any) => {
          tasksArray.push(this.fb.group({
            description: [t.description, Validators.required],
            status: [t.status || 'PENDING'],
            notes: [t.notes || ''],
            originalTaskId: [t.originalTaskId || null]
          }));
        });
      }
    }

    get eventConsumables() {
      return this.eventForm.get('consumables') as FormArray;
    }

    get stockConsumablesCount(): number {
      return this.eventConsumables?.controls?.filter(c => !c.get('is_external')?.value).length || 0;
    }

    get externalConsumablesCount(): number {
      return this.eventConsumables?.controls?.filter(c => !!c.get('is_external')?.value).length || 0;
    }

    get eventCosts() {
      return this.eventForm.get('costs') as FormArray;
    }

    get eventTotalsByCurrency(): {currency: string, amount: number}[] {
      const totals = new Map<string, number>();
      
      const fixedCost = this.eventForm.get('cost')?.value || 0;
      const fixedCostCur = this.eventForm.get('cost_currency')?.value || 'ARS';
      if (fixedCost > 0) totals.set(fixedCostCur, (totals.get(fixedCostCur) || 0) + fixedCost);

      const consumables = this.eventConsumables.value || [];
      consumables.forEach((c: any) => {
        const unitCost = c.unit_cost || (this.internalConsumablesMap?.get(c.itemId)?.price) || 0;
        const currency = c.unit_cost_currency || (this.internalConsumablesMap?.get(c.itemId)?.currency) || 'ARS';
        const amount = (c.quantity || 0) * unitCost;
        if (amount > 0) totals.set(currency, (totals.get(currency) || 0) + amount);
      });

      const costs = this.eventCosts.value || [];
      costs.forEach((c: any) => {
        const currency = c.currency || 'ARS';
        const amount = c.amount || 0;
        if (amount > 0) totals.set(currency, (totals.get(currency) || 0) + amount);
      });

      return Array.from(totals.entries()).map(([currency, amount]) => ({ currency, amount }));
    }

    addConsumable(isExternal: boolean = false) {
      const consGroup = this.fb.group({
        itemId: [null],
        name: ['', isExternal ? Validators.required : []],
        quantity: [1, [Validators.required, Validators.min(0.01)]],
        is_external: [isExternal],
        stock: [null],
        available_stock: [null],
        unit_cost: [null],
        unit_cost_currency: ['ARS'],
        stockMovementId: [null],
        movement_code: [null]
      });

      this.eventConsumables.push(consGroup);
      if (isExternal) {
        this.activeTab = 2;
        this.consumableTab = 1;
      }
    }

    onInternalSelect(itemId: string) {
      if (!itemId) return;
      const item = this.internalConsumablesMap.get(itemId);
      if (!item) return;

      const exists = this.eventConsumables.controls.some(c => c.get('itemId')?.value === itemId);
      if (exists) {
        this.messageService.add({ severity: 'warn', summary: 'Ya agregado', detail: 'Este insumo ya está en la lista' });
        this.selectedInternalItem = null;
        return;
      }

      const available = (item.stock || 0) - (item.reserved_stock || 0) - (item.blocked_stock || 0);
      const consGroup = this.fb.group({
        itemId: [item.id, Validators.required],
        name: [item.name],
        quantity: [1, [Validators.required, Validators.min(0.01)]],
        is_external: [false],
        stock: [item.stock],
        available_stock: [available],
        unit_cost: [item.price || 0],
        unit_cost_currency: [item.currency || 'ARS'],
        stockMovementId: [null],
        movement_code: [null]
      });

      this.eventConsumables.push(consGroup);
      this.selectedInternalItem = null;
    }

  removeConsumable(index: number) {
    this.eventConsumables.removeAt(index);
  }

  addCost() {
    const costGroup = this.fb.group({
      conceptId: [null, Validators.required],
      amount: [0, [Validators.required, Validators.min(0.01)]],
      currency: ['ARS']
    });
    this.eventCosts.push(costGroup);
  }

  removeCost(index: number) {
    this.eventCosts.removeAt(index);
  }

  get eventTasks() {
    return this.eventForm.get('tasks') as FormArray;
  }

  async loadDeferredTasks() {
    if (!this.equipmentId) return;
    this.loadingDeferred = true;
    try {
      this.deferredTasks = await this.inventoryService.getDeferredTasks(this.equipmentId);
    } catch (e) {
      console.error(e);
    } finally {
      this.loadingDeferred = false;
    }
  }

  addDeferredTask(task: any, index: number) {
    const tasksArray = this.eventTasks;
    tasksArray.push(this.fb.group({
      description: [task.description, Validators.required],
      status: ['PENDING'],
      notes: [''],
      originalTaskId: [task.id]
    }));
    this.deferredTasks.splice(index, 1);
  }

  async discardDeferredTask(task: any, index: number) {
    try {
      await this.inventoryService.discardTask(task.id);
      this.deferredTasks.splice(index, 1);
      this.messageService.add({ severity: 'success', summary: 'Descartada', detail: 'Tarea descartada definitivamente' });
    } catch (e) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudo descartar la tarea' });
    }
  }

  addTask() {
    const taskGroup = this.fb.group({
      description: ['', Validators.required],
      status: ['PENDING'],
      notes: [''],
      originalTaskId: [null]
    });
    this.eventTasks.push(taskGroup);
  }

  removeTask(index: number) {
    this.eventTasks.removeAt(index);
  }

  onTaskCheck(index: number, checked: boolean) {
    const task = this.eventTasks.at(index);
    task.get('status')?.setValue(checked ? 'COMPLETED' : 'PENDING');
    if (checked) {
       task.get('notes')?.setValue('');
    }
  }

  toggleTaskSecondaryStatus(index: number, status: 'DEFERRED' | 'SKIPPED') {
    const task = this.eventTasks.at(index);
    if (task.get('status')?.value === status) {
       task.get('status')?.setValue('PENDING');
    } else {
       task.get('status')?.setValue(status);
    }
  }

  close() {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  printSheet(): void {
    const formVal = this.eventForm.getRawValue();
    this.printService.printEventSheet({
      appSettings: this.configService.appSettings,
      eventType: this.eventType,
      equipment: this.equipment ?? {},
      event: {
        ...this.editData,
        ...formVal,
        tasks: (formVal.tasks || []).map((t: any) => ({
          description: t.description || t.originalTaskId || '',
          status: t.status || 'PENDING',
          notes: t.notes || '',
        })),
        consumables: (formVal.consumables || []),
        supplier: this.editData?.supplier,
      }
    });
  }

  switchToCompleteMode() {
    this.setEventMode('COMPLETE');
    this.messageService.add({ severity: 'info', summary: 'Modo Ejecución', detail: 'El formulario ahora muestra los campos de cierre. Completá la información y guardá para finalizar la intervención.' });
  }

  async save() {
    if (this.eventForm.invalid) {
      // Find invalid fields for debugging
      const invalidFields = [];
      const controls = this.eventForm.controls;
      for (const name in controls) {
        if (controls[name].invalid) {
          invalidFields.push(name);
        }
      }
      console.log('Invalid fields:', invalidFields);
      this.messageService.add({ 
        severity: 'error', 
        summary: 'Campos Incompletos', 
        detail: 'Faltan completar campos obligatorios marcados en rojo.' 
      });
      this.eventForm.markAllAsTouched();
      return;
    }
    
    try {
      this.loading = true;
      const formValue = this.eventForm.getRawValue();
      const consumables = (formValue.consumables || [])
        .filter((c: any) => !c.is_external && c.itemId)
        .map((c: any) => ({
          itemId: c.itemId,
          quantity: Math.round(c.quantity),
          unit_cost_currency: c.unit_cost_currency || 'ARS'
        }));

      const payload: any = {
        equipmentId: this.equipmentId,
        event_date: formValue.event_date,
        description: formValue.description,
        external_service: formValue.external_service,
        supplierId: formValue.supplierId || undefined,
        notes: formValue.notes || undefined,
        cost: formValue.cost ? Number(formValue.cost) : undefined,
        cost_currency: formValue.cost_currency || undefined,
        costs: formValue.costs || [],
        tasks: formValue.tasks || [],
        consumables,
        auto_schedule_next: formValue.auto_schedule_next,
        replace_existing_scheduled: formValue.replace_existing_scheduled,
        parentEventId: formValue.parentEventId || undefined,
        duration_hours: formValue.duration_hours || undefined,
        end_date: formValue.end_date || undefined,
        status: this.eventMode === 'SCHEDULE' ? 'SCHEDULLED' : 'SUCCESSFULL'
      };

      if (this.eventType === 'MAINTENANCE') {
        payload.type = formValue.type;
        payload.new_status = formValue.new_status || undefined;
      } else {
        payload.type = 'CALIBRATION';
        payload.certificate_number = formValue.certificate_number || undefined;
        payload.result_approved = formValue.result_approved;
        payload.performed_by = formValue.performed_by || undefined;
        payload.approved_by = formValue.approved_by || undefined;
        payload.next_date_manual = formValue.next_date_manual || undefined;
      }

      const isEdit = !!this.editData;
      if (isEdit) {
        payload.id = this.editData.id;
        if (this.eventType === 'MAINTENANCE') {
          await this.inventoryService.updateMaintenance(payload.id, payload);
        } else {
          await this.inventoryService.updateCalibration(payload.id, payload);
        }
        this.messageService.add({ severity: 'success', summary: 'Actualizado', detail: `${this.eventType === 'MAINTENANCE' ? 'Mantenimiento' : 'Calibración'} actualizada` });
      } else {
        if (this.eventType === 'MAINTENANCE') {
          await this.inventoryService.createMaintenance(payload);
        } else {
          await this.inventoryService.createCalibration(payload);
        }
        this.messageService.add({ severity: 'success', summary: 'Cargado', detail: `${this.eventType === 'MAINTENANCE' ? 'Mantenimiento' : 'Calibración'} registrada` });
      }

      this.close();
      this.onSaved.emit();
    } catch (e) {
      console.error(e);
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar el evento' });
    } finally {
      this.loading = false;
    }
  }
}
